import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { GetPackageBundleProps } from "_interfaces/http-requests/packageBundle";
import { PackageBundleDetailsProps } from "_interfaces/phoneBooking";
import { BundlePlansFiltersProps } from "_interfaces/products/bundle-plans";
import PlanCardFour from "component/_common/plans/planCardFour";
import { filterNonNullValues } from "functions/helper";
import { PackageBundleGetAll } from "functions/http-requests/packageBundle";
import RouteConstant from "navigation/constant";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toggleLoading } from "state/slice/loading";
import { DispatchRechargeData } from "state/slice/rechargePlan";
import { RootState } from "state/store";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

interface Props {
  filters: BundlePlansFiltersProps;
  handleSelectPlan: () => void;
}

const AllBundlePlans: React.FC<Props> = ({ filters, handleSelectPlan }) => {
  const navigate = useNavigate();
  const Dispatch = useDispatch();
  const pathname = useLocation()?.pathname;
  const [searchParams] = useSearchParams();
  const packageId = searchParams?.get("package");

  const paginationRef = useRef<HTMLDivElement | null>(null);

  const { isLoggedIn } = useSelector((state: RootState) => state.user);

  const [state, setState] = useState<PackageBundleDetailsProps[]>([]);

  useEffect(() => {
    let fetchPlans: (() => void) | null = () => {
      Dispatch(toggleLoading(true));

      let PAYLOAD_DATA: GetPackageBundleProps["DATA"] = {
        pageNumber: 1,
        pageSize: 100,
        isActive: true,
        packageFor: filters?.packageFor || undefined,
        packageType: filters?.packageType?.length
          ? filters?.packageType
          : undefined,
        packageValidity: filters?.packageValidity || undefined,
        unlimited: filters?.unlimited || undefined,
      };
      PAYLOAD_DATA = filterNonNullValues(PAYLOAD_DATA);

      PackageBundleGetAll({
        DATA: PAYLOAD_DATA,
      })
        .then((res) => {
          const DATA = res?.data?.data;
          if (DATA?.length) {
            setState(
              DATA?.map((item: any) => ({
                _id: item?._id || "",
                retailPrice: item?.retailPrice || "",
                packageName: item?.packageName || "",
                category: item?.category || "",
                packageType: item?.packageType || "",
                isValidityUnlimited: item?.isValidityUnlimited || false,
                validity: item?.validity || "",
                allNetMinutes: item?.allNetMinutes || "",
                onNetMinutes: item?.onNetMinutes || "",
                offNetMinutes: item?.offNetMinutes || "",
                allNetSMS: item?.allNetSMS || "",
                onNetSMS: item?.onNetSMS || "",
                offNetSMS: item?.offNetSMS || "",
                internationalCallMinutes: item?.internationalCallMinutes || "",
                packageFor: item?.packageFor || "",
                packageID: item?.packageID || "",
                dataPerDay: item?.dataPerDay || null,
                dataVolume: item?.dataVolume || null,
              })),
            );
          } else {
            setState([]);
          }
        })
        .catch(() => {
          setState([]);
        })
        .finally(() => {
          Dispatch(toggleLoading(false));
        });
    };
    fetchPlans();

    return () => {
      fetchPlans = null;
    };
  }, [filters]);

  useEffect(() => {
    if (packageId && state?.length) {
      const matchedPackage = state?.find((item) => item?._id === packageId);
      if (matchedPackage && handleSelectPlan) {
        Dispatch(DispatchRechargeData({ data: matchedPackage }));
        handleSelectPlan();
      }
    }
  }, [packageId, state]);

  return (
    <>
      <Box position="relative">
        {state?.length ? (
          <>
            <Swiper
              spaceBetween={30}
              pagination={{
                clickable: true,
                el: ".swiper-pagination",
              }}
              modules={[Pagination]}
              className="custom-pagination-swiper"
              centerInsufficientSlides
              freeMode
              breakpoints={{
                0: {
                  slidesPerView: 1,
                  slidesPerGroup: 1,
                },
                600: {
                  slidesPerView: 2,
                  slidesPerGroup: 2,
                },
                900: {
                  slidesPerView: 3,
                  slidesPerGroup: 3,
                },
                1100: {
                  slidesPerView: 4,
                  slidesPerGroup: 4,
                },
                1400: {
                  slidesPerView: 5,
                  slidesPerGroup: 5,
                },
              }}
              style={{
                paddingTop: "24px",
                paddingBottom: "50px",
                paddingLeft: "24px",
                paddingRight: "24px",
              }}
              onSlideChange={() => {
                const activeBullet = document.querySelector(
                  ".swiper-pagination-bullet-active",
                ) as HTMLElement | null;

                if (paginationRef.current && activeBullet) {
                  paginationRef.current.scrollLeft =
                    activeBullet.offsetLeft -
                    paginationRef.current.offsetWidth / 2 +
                    activeBullet.offsetWidth / 2;
                }
              }}
            >
              {state.map((item: any, i: number) => (
                <SwiperSlide key={i}>
                  <PlanCardFour
                    data={item}
                    handleClick={() => {
                      if (!isLoggedIn) {
                        navigate(
                          `${RouteConstant.LOGIN}?redirect-url=${pathname}&package=${item?._id}`,
                        );
                        return;
                      }
                      Dispatch(DispatchRechargeData({ data: item }));
                      if (handleSelectPlan) handleSelectPlan();
                    }}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            <Box className="swiper-pagination" ref={paginationRef} />
          </>
        ) : (
          <Box pt={3}>
            <Typography fontSize={{ xs: 16, md: 24 }} textAlign="center">
              No Plan Found
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AllBundlePlans;
